import Popover from "@mui/material/Popover";
import classNames from "classnames";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { EditIcon } from "components/Icons";
import { WithSearch } from "components/OutlinedSelect/OutlinedSelect";

import { SelectItemsWithBulkActions } from "./SelectItems";

import styles from "./styles.module.scss";

type Props = {
  onChange: (privileges: string[]) => void;
  privileges: { text: string; value: string }[];
  resource: string;
  index: number;
  groupState: {
    toAssign: string[];
    toDeny: string[];
    objects: { catalogName: string; name: string }[] | string[];
    expanded?: boolean;
  };
};

export const GrantPrivilegesSelector = (props: Props) => {
  const { t } = useTranslation();
  const { onChange, privileges, index, resource, groupState } = props;

  const selectorRef = useRef<HTMLDivElement | null>(null);
  const { menuElement, openMenu, closeMenu, iconElement } = useMenu();
  const isViewOnly = useViewOnly();
  const { toAssign, toDeny } = groupState;
  const items = privileges.map(privilege => {
    const { text, value } = privilege;
    const disabled = toDeny.includes(value);
    const disabledTooltip = disabled
      ? t("roles_wizard.privileges.disabled_revoke_privilege_tooltip", {
          privilege: text,
        })
      : undefined;
    return (
      <ContextMenuItem
        testId={`add-privilege-${value}`}
        key={value}
        value={value}
        checked={toAssign.includes(value)}
        checkedIconPlaceholder={true}
        text={text}
        direction="left"
        disabled={disabled || isViewOnly}
        disabledTooltip={disabledTooltip}
        onClick={() => {
          if (toAssign.includes(value)) {
            onChange(toAssign.filter(item => item !== value));
          } else {
            onChange([...toAssign, value]);
          }
        }}
      />
    );
  });

  return (
    <div
      className={styles.grantSelector}
      ref={selectorRef}
    >
      {toAssign.length ? (
        <div className={styles.badges}>
          {toAssign.map(privilege => {
            const value = privilege.toLowerCase();
            return (
              <div
                key={privilege}
                className={styles.badge}
              >
                {value}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className={styles.placeholder}
          onClick={openMenu}
          data-testid={`roles-privileges-${resource}-placeholder`}
        >
          {t("roles_wizard.privileges.add_permissions")}
        </div>
      )}
      {!!toAssign.length && (
        <div
          className={classNames(styles.edit, {
            [styles.edit__active]: !!menuElement,
          })}
          onClick={openMenu}
          data-testid={`roles-wizard-grant-privileges-${resource}-${index}`}
          ref={iconElement}
        >
          <EditIcon />
        </div>
      )}
      {!!menuElement && (
        <Popover
          open
          anchorEl={
            toAssign.length && iconElement.current
              ? iconElement.current
              : selectorRef.current
          }
          classes={{ paper: styles.paper }}
          onClose={closeMenu}
          marginThreshold={8}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              style: {
                marginTop: 2,
              },
            },
          }}
          data-testid="grant-privileges-selector"
        >
          <WithSearch
            items={items}
            searchOptions={{
              searchPlaceholder: t("roles_wizard.privileges.search"),
              searchKey: "text",
            }}
          >
            <SelectItemsWithBulkActions
              toAssign={toAssign}
              onChange={onChange}
            />
          </WithSearch>
        </Popover>
      )}
    </div>
  );
};
